import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import {
  Pane,
  Paneset,
  Button,
  Loading,
  Icon,
  Layout,
  PaneFooter,
  Selection,
} from '@folio/stripes/components';
import { AppIcon } from '@folio/stripes/core';
import FormFieldsGenerator from '../../../shared/FormFieldsGenerator';
import BulkEditProgressModal from './BulkEditProgressModal';
import useBulkEditInstances from '../../../hooks/useBulkEditInstances';
import useControlledVocab from '../../../hooks/useDataQueries';

const BulkEditInstancesPage = ({ basePath }) => {
  const location = useLocation();
  const history = useHistory();
  const intl = useIntl();
  const [instanceIds, setInstanceIds] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templateChanged, setTemplateChanged] = useState(false);

  const { isProcessing, progress, results, processBulkEdit, reset } =
    useBulkEditInstances();
  const { data: controlledVocab, pending } = useControlledVocab();

  const defaultTemplate = useMemo(() => {
    if (!pending && controlledVocab?.templates) {
      return controlledVocab.templates.find((t) => t.isDefault === true);
    }
    return null;
  }, [pending, controlledVocab]);

  const currentTemplate = useMemo(() => {
    const templates = controlledVocab?.templates || [];
    return (
      templates.find((t) => t.id === selectedTemplateId) || defaultTemplate
    );
  }, [selectedTemplateId, controlledVocab, defaultTemplate]);

  const filteredTemplate = useMemo(() => {
    return currentTemplate?.templateFields?.instance?.map((section) => ({
      ...section,
      sectionFields: section.sectionFields.map((field) => ({
        ...field,
        isRequired: false,
        canEdit:
          field.name !== 'title' && field.name !== 'alternativeTitles.value',
      })),
    }));
  }, [currentTemplate]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlIds = searchParams.get('ids');

    if (urlIds) {
      const ids = urlIds.split(',').filter((id) => id.trim());
      setInstanceIds(ids);
    } else {
      const storedIds = sessionStorage.getItem('bulkEditInstanceIds');
      if (storedIds) {
        try {
          const ids = JSON.parse(storedIds);
          setInstanceIds(Array.isArray(ids) ? ids : []);
          sessionStorage.removeItem('bulkEditInstanceIds');
        } catch (error) {
          console.error('Error parsing stored instance IDs:', error);
          setInstanceIds([]);
        }
      }
    }
  }, [location]);

  useEffect(() => {
    if (defaultTemplate && !selectedTemplateId) {
      setSelectedTemplateId(defaultTemplate.id);
    }
  }, [defaultTemplate, selectedTemplateId]);

  const handleFormSubmit = async (values) => {
    const updates = { ...values };

    if (templateChanged && selectedTemplateId) {
      updates.templateTypeId = selectedTemplateId;
    }

    setShowModal(true);
    await processBulkEdit(instanceIds, updates);
  };

  const handleCancel = () => {
    history.goBack();
  };

  const handleModalClose = () => {
    setShowModal(false);
    reset();
    history.goBack();
  };

  if (instanceIds.length === 0) {
    return (
      <Paneset>
        <Pane
          defaultWidth="100%"
          paneTitle={
            <FormattedMessage
              id="ui-plugin-inventory-templates.bulkEdit.paneTitle"
              defaultMessage="Bulk Edit Instances ({count})"
              values={{ count: 0 }}
            />
          }
        >
          <Layout className="textCentered">
            <Icon icon="exclamation-circle" size="large" />
            <p>
              <FormattedMessage
                id="ui-plugin-inventory-templates.bulkEdit.noInstancesSelected"
                defaultMessage="No instances selected for bulk edit"
              />
            </p>
            <Button buttonStyle="primary" onClick={() => history.goBack()}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.bulkEdit.backToInventory"
                defaultMessage="Back to Inventory"
              />
            </Button>
          </Layout>
        </Pane>
      </Paneset>
    );
  }

  return (
    <>
      <Form
        onSubmit={handleFormSubmit}
        mutators={{ ...arrayMutators }}
        initialValues={{
          discoverySuppress: false,
          staffSuppress: false,
          previouslyHeld: false,
        }}
      >
        {({ handleSubmit, pristine, submitting, form }) => (
          <form onSubmit={handleSubmit}>
            <Paneset>
              <Pane
                defaultWidth="100%"
                paneTitle={
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.bulkEdit.paneTitle"
                    defaultMessage="Bulk Edit Instances ({count})"
                    values={{ count: instanceIds.length }}
                  />
                }
                footer={
                  <PaneFooter
                    renderStart={
                      <Button onClick={handleCancel}>
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.bulkEdit.cancel"
                          defaultMessage="Cancel"
                        />
                      </Button>
                    }
                    renderEnd={
                      <Button
                        buttonStyle="primary"
                        type="submit"
                        disabled={pristine || submitting || pending}
                      >
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.bulkEdit.apply"
                          defaultMessage="Apply Changes ({count})"
                          values={{ count: instanceIds.length }}
                        />
                      </Button>
                    }
                  />
                }
              >
                {pending ? (
                  <Layout className="textCentered">
                    <Loading />
                  </Layout>
                ) : (
                  <>
                    <div
                      style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        padding: '12px 16px',
                        marginBottom: '20px',
                        backgroundColor: '#e7f4ff',
                        borderLeft: '4px solid #0b7ad1',
                        borderRadius: '4px',
                      }}
                    >
                      <p
                        style={{ margin: 0, color: '#222', fontWeight: 'bold' }}
                      >
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.bulkEdit.formModal.description"
                          defaultMessage="Fill in only the fields you want to update. Empty fields will not be changed."
                        />
                      </p>
                    </div>

                    <div style={{ padding: '0 16px' }}>
                      <div style={{ marginBottom: '20px' }}>
                        <Field name="templateTypeId">
                          {({ input }) => (
                            <Selection
                              {...input}
                              value={selectedTemplateId || ''}
                              dataOptions={[
                                { label: '', value: '' },
                                ...(controlledVocab?.templates || []).map(
                                  (t) => ({
                                    label: t.name?.find(
                                      (n) => n.locale === intl.locale,
                                    )?.value,
                                    value: t.id,
                                  }),
                                ),
                              ]}
                              label={
                                <FormattedMessage
                                  id="ui-plugin-inventory-templates.bulkEdit.field.templateType"
                                  defaultMessage="Template Type"
                                />
                              }
                              onChange={(value) => {
                                input.onChange(value);
                                setSelectedTemplateId(value);
                                setTemplateChanged(true);
                              }}
                            />
                          )}
                        </Field>
                      </div>

                      {filteredTemplate && (
                        <FormFieldsGenerator
                          formValues={filteredTemplate}
                          controlledVocab={controlledVocab}
                        />
                      )}
                    </div>
                  </>
                )}
              </Pane>
            </Paneset>
          </form>
        )}
      </Form>

      <BulkEditProgressModal
        open={showModal}
        progress={progress}
        isProcessing={isProcessing}
        results={results}
        onClose={handleModalClose}
      />
    </>
  );
};

BulkEditInstancesPage.propTypes = {
  basePath: PropTypes.string.isRequired,
};

export default BulkEditInstancesPage;
